import { Key, ReactNode, useEffect, useMemo, useState } from 'react';

import { Box, Stack } from '@mui/material';
import Tree, { DataNode } from 'antd/lib/tree';
import { Element } from 'react-scroll';

import { SVGAsset } from 'components/atoms/SVGAsset';
import { onSelectFunc } from 'components/organisms/PageEditor/editorControls/NodeTree/types';
import {
    findParentKeys,
    getParentKey,
} from 'components/organisms/PageEditor/editorControls/NodeTree/utils';
import { PageList } from 'types/api/pageList';
import { highlightInText } from 'utils/highlightInText';

type PageSelectTreeProps = {
    pages: PageList[];
    selectedPage?: number;
    onPageSelect: (id: number, href: string) => void;
    searchValue?: string;
};

type NodeWithRouteUuid = DataNode & {
    route_uuid?: string;
    uuid?: string;
    label?: PageList['label'];
    is_fundamental?: PageList['is_fundamental'];
    href?: string;
    children?: NodeWithRouteUuid[] & {
        href?: string;
    };
};

const getTreeData = (
    items: PageList[],
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    selected?: number
): NodeWithRouteUuid[] => {
    if (items && items.length > 0) {
        const data: NodeWithRouteUuid[] = [];
        const map: {
            [key: string]: NodeWithRouteUuid;
        } = {};

        items.forEach((item) => {
            const { uuid } = item;

            if (!Object.hasOwn(map, uuid)) {
                map[uuid] = {
                    key: item.id,
                    uuid: item.uuid,
                    title: item.title,
                    route_uuid: item.route_uuid || '',
                    selectable: !item.is_static && !item.is_fundamental,
                    label: item.label,
                    disabled: false,
                    children: [],
                    checkable: false,
                    is_fundamental: item.is_fundamental,
                    href: item.href,
                };
            } else {
                map[uuid] = {
                    key: item.id,
                    uuid: item.uuid,
                    title: item.title,
                    route_uuid: item.route_uuid || '',
                    selectable: !item.is_static && !item.is_fundamental,
                    label: item.label,
                    disabled: false,
                    children: [],
                    checkable: false,
                    is_fundamental: item.is_fundamental,
                    href: item.href,
                };
            }
        });

        Object.keys(map).forEach((id) => {
            if (Object.hasOwn(map, id)) {
                const mappedElem: NodeWithRouteUuid = map[id];
                if (
                    mappedElem.route_uuid &&
                    typeof map[mappedElem.route_uuid] !== 'undefined'
                ) {
                    map[mappedElem.route_uuid].children?.push(mappedElem);
                } else {
                    data.push(mappedElem);
                }
            }
        });

        return data;
    }
    return [];
};

type PageTitleWrapperProps = {
    children?: ReactNode;
    id: string | number;
};

const PageTitleWrapper = ({
    children,
    id,
}: PageTitleWrapperProps): JSX.Element => {
    return (
        <Element name={`scroll_el_${id}`} id="scroll_el">
            <Stack alignItems="center" direction="row">
                <Box>{children}</Box>
            </Stack>
        </Element>
    );
};

export const PageSelectTree = ({
    pages,
    onPageSelect,
    selectedPage,
    searchValue = '',
}: PageSelectTreeProps): JSX.Element => {
    const [expandedKeys, setExpandedKeys] = useState<Key[]>([]);
    const [autoExpandParent, setAutoExpandParent] = useState(true);

    const nodeTrees = useMemo(() => {
        return getTreeData(pages, selectedPage);
    }, [pages, selectedPage]);

    useEffect(() => {
        if (searchValue !== '') {
            const newExpandedKeys = pages
                .map((item) => {
                    if (
                        item.title
                            .toLowerCase()
                            .indexOf(searchValue.toLowerCase()) > -1
                    ) {
                        return getParentKey(item.id, nodeTrees);
                    }
                    return null;
                })
                .filter(
                    (item, i, self) => item && self.indexOf(item) === i
                ) as Key[];

            setExpandedKeys(Array.from(new Set([...newExpandedKeys])));
        }
        if (selectedPage) {
            const newExpandedKeys = findParentKeys(selectedPage, nodeTrees);

            setExpandedKeys((keys) => {
                return Array.from(new Set([...keys, ...newExpandedKeys]));
            });
        }
        setAutoExpandParent(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchValue, selectedPage, nodeTrees]);

    const treeData = useMemo(() => {
        const loop = (data: NodeWithRouteUuid[]): NodeWithRouteUuid[] =>
            data.map((item) => {
                const title = highlightInText(
                    item.title as string,
                    searchValue
                );

                const parentsKeys = selectedPage
                    ? findParentKeys(selectedPage, nodeTrees)
                    : [];

                if (item.children) {
                    return {
                        ...item,
                        title: (
                            <PageTitleWrapper id={item.key}>
                                {title.element}
                            </PageTitleWrapper>
                        ),
                        className: `${title.match ? 'indexed' : ''} ${
                            parentsKeys.includes(item.key) ? 'parent-nodes' : ''
                        } ${parentsKeys.length > 1 ? 'deepest-leaf' : ''} ${
                            findParentKeys(item.key, nodeTrees).length === 0
                                ? 'first-level'
                                : ''
                        } ${
                            item.children?.length === 0
                                ? 'empty-children'
                                : 'not-empty-children'
                        }`,
                        children: loop(item.children),
                        href: item.href,
                        style:
                            searchValue !== '' && !title.match
                                ? { display: 'none' }
                                : {},
                    };
                }

                return {
                    ...item,
                    className: title.match ? 'indexed' : undefined,
                    title: (
                        <PageTitleWrapper id={item.key}>
                            {title.element}
                        </PageTitleWrapper>
                    ),
                    href: item.href,
                };
            });

        return loop(nodeTrees);
    }, [nodeTrees, searchValue, selectedPage]);

    const onExpand = (newExpandedKeys: Key[]): void => {
        setExpandedKeys(Array.from(new Set([...newExpandedKeys])));
        setAutoExpandParent(false);
    };

    const onNodeSelect: onSelectFunc = (keys): void => {
        if (keys.length === 1 && selectedPage !== keys[0]) {
            if (selectedPage) {
                const parentsCount = findParentKeys(
                    selectedPage,
                    nodeTrees
                ).length;

                setExpandedKeys(expandedKeys.slice(-parentsCount));
                setAutoExpandParent(false);
            }

            onPageSelect(
                pages.filter((el) => el.id === Number(keys[0]))[0].id,
                pages.filter((el) => el.id === Number(keys[0]))[0].href
            );
        }
    };

    return (
        <Tree
            blockNode
            showLine
            treeData={treeData}
            switcherIcon={<SVGAsset src="icons.24px.chevron" />}
            selectedKeys={selectedPage ? [selectedPage] : undefined}
            onSelect={onNodeSelect}
            expandedKeys={expandedKeys}
            autoExpandParent={autoExpandParent}
            onExpand={onExpand}
            onClick={(e, node) => {
                if (!expandedKeys.includes(node.key)) {
                    if (node.children && node.children.length) {
                        setExpandedKeys((keys) => [...keys, node.key]);
                    }
                } else {
                    setExpandedKeys((keys) =>
                        keys.filter((el) => el !== node.key)
                    );
                }
            }}
        />
    );
};
